import { getDisplayableSessionGist } from '@/constants/session_summary';
import { type SimpleSessionResponse } from '@heidi/session/contracts';
import { type IFuseOptions } from 'fuse.js';

/** Debounce delay for session search input (ms) */
export const SESSION_SEARCH_DEBOUNCE_MS = 250;

export const ehrFuseSettings = {
  threshold: 0.2,
  keys: [
    'patient_first_name',
    'patient_last_name',
    'patient.name.0.given',
    'patient.name.0.family',
  ],
};

// Base search keys used for all session types
const sessionSearchKeys = [
  'session_id',
  'patient.demographic_details',
  'session_name',
  'session_gist',
  'patient.name',
];

// TODO: Remove createSessionFuseSettings and getSearchableDisplayName once backend search is implemented.
// This is a temporary client-side workaround to allow searching for sessions
// that display as "Untitled session" in the UI (when they have no patient info or gist).

/**
 * Computes a searchable display name for a session.
 * This is used for Fuse.js search so that users can find "untitled" sessions.
 * @param untitledSessionText - The translated "Untitled session" text from i18n
 */
const getSearchableDisplayName = (
  session: SimpleSessionResponse | undefined,
  untitledSessionText: string
): string => {
  // Fuse's own getter bails on an absent doc; a custom `getFn` gets called
  // regardless, so this guard is the equivalent. A row that is not there has no
  // searchable text — '' is skipped when the index is built, where returning
  // the untitled text would make the row match a search it cannot render
  // (SCR-7433).
  if (!session) return '';

  return (
    session.patient?.demographic_details ||
    session.patient?.name ||
    getDisplayableSessionGist(session.session_gist) ||
    untitledSessionText
  );
};

/**
 * Creates Fuse.js search configuration for online sessions (SimpleSessionResponse).
 * Uses getFn to dynamically compute searchableDisplayName without modifying original data.
 * @param untitledSessionText - The translated "Untitled session" text from i18n (e.g., t('shared.UntitledSession'))
 */
export const createSessionFuseSettings = (
  untitledSessionText: string
): IFuseOptions<SimpleSessionResponse> => ({
  threshold: 0.2,
  keys: [
    ...sessionSearchKeys,
    {
      name: 'searchableDisplayName',
      getFn: (session) => getSearchableDisplayName(session, untitledSessionText),
    },
  ],
});

/**
 * Fuse.js search configuration for offline sessions.
 * Does not include searchableDisplayName as offline sessions have different structure.
 */
export const offlineSessionFuseSettings = {
  threshold: 0.2,
  keys: sessionSearchKeys,
};
